import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, ShieldCheck, Globe } from 'lucide-react';
import blogPosts from '../data/blogPosts';

const Footer = () => {
  const latestPosts = blogPosts.slice(0, 4);

  const footerLinks = {
    Product: [
      { label: 'Forsyt vs Gong', href: '/vs-gong' },
      { label: 'Forsyt vs Clari', href: '/vs-clari' },
      { label: 'Forsyt vs Scratchpad', href: '/vs-scratchpad' },
      { label: 'Buy vs Build', href: '/buy-vs-build' }
    ],
    Resources: [
      { label: 'Blog', href: '/blog' },
      { label: 'Origin Story', href: '/origin-story' },
      { label: 'Security', href: '/security' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Refund Policy', href: '/refund' }
    ]
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Logo and Description */}
          <div className="lg:col-span-2">
            <img
              src="/assets/5xrpfj9a.png"
              alt="Forsyt"
              className="h-[52px] w-auto mb-4"
            />
            <p className="text-sm text-gray-400 mb-6 max-w-sm">
              Forsyt is the AI‑native Revenue Operating System that turns your scattered GTM stack into one stream of prioritized, revenue‑driving actions.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.linkedin.com/company/113321920"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-blue-600 flex items-center justify-center transition-colors duration-300"
                aria-label="Forsyt on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <nav key={category} aria-label={`${category} links`}>
              <h3 className="font-semibold text-white mb-4">{category}</h3>
              <ul className="space-y-3">
                {category === 'Resources' && (
                  <>
                    <li>
                      <Link
                        to="/plugins"
                        data-testid="footer-plugins-link"
                        className="inline-flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200 transition-colors duration-200 font-semibold"
                      >
                        Micro SaaS Store
                        <span className="text-[9px] font-bold uppercase tracking-wider text-blue-200 bg-blue-500/20 border border-blue-400/30 px-1.5 py-0.5 rounded-full">New</span>
                      </Link>
                    </li>
                  </>
                )}
                {links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith('/') ? (
                      <Link
                        to={link.href}
                        className="text-sm hover:text-blue-400 transition-colors duration-200"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-sm hover:text-blue-400 transition-colors duration-200"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          {/* Latest Articles */}
          <nav aria-label="Latest articles">
            <h3 className="font-semibold text-white mb-4">Latest Articles</h3>
            <ul className="space-y-3">
              {latestPosts.map((post) => (
                <li key={post.id}>
                  <Link
                    to={`/blog/${post.id}`}
                    className="text-sm hover:text-blue-400 transition-colors duration-200 line-clamp-1"
                  >
                    {post.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500 order-2 md:order-1">
              © {new Date().getFullYear()} Credos Forsyt Private Limited. All rights reserved. &middot; New Delhi, India
            </p>
            <div className="flex items-center gap-2 order-1 md:order-2" data-testid="footer-compliance-badges">
              <Link
                to="/security"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 hover:border-emerald-400 transition-colors"
                aria-label="ISO 27001 certified"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] font-bold tracking-widest text-emerald-300 uppercase">ISO 27001</span>
              </Link>
              <Link
                to="/security#gdpr"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 hover:border-blue-400 transition-colors"
                aria-label="GDPR compliant"
              >
                <Globe className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] font-bold tracking-widest text-blue-300 uppercase">GDPR</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
